{
  "app": {
    "name": "Partner space",
    "skipToContent": "Skip to content",
    "logoAlt": "Proxy Seller"
  },
  "nav": {
    "label": "Partner space sections",
    "open": "Open menu",
    "close": "Close menu",
    "dashboard": "Overview",
    "statistics": "Statistics",
    "sdk": "SDK",
    "moderation": "Moderation",
    "plugin": "Grafana plugin",
    "faq": "FAQ"
  },
  "session": {
    "signOut": "Sign out",
    "account": "Account"
  },
  "partner": {
    "company": "Company",
    "companyUnknown": "No company on file",
    "pendingTitle": "Application in review",
    "pendingDescription": "We are reviewing your application. The SDK and the FAQ are open meanwhile — enough to start integrating.",
    "loadFailed": "Could not load your profile"
  },
  "common": {
    "loading": "Loading",
    "retry": "Try again",
    "cancel": "Cancel",
    "close": "Close",
    "refresh": "Refresh",
    "country": "Country",
    "status": "Status",
    "copy": "Copy",
    "copied": "Copied",
    "search": "Search",
    "of": "of"
  },
  "errors": {
    "unexpectedTitle": "Something broke on our side",
    "unexpectedDescription": "We logged it. Reload the page — nothing was lost.",
    "networkTitle": "No connection to the server",
    "networkDescription": "Check your connection and try again.",
    "serverTitle": "The server did not answer",
    "serverDescription": "Try again in a minute. If it keeps happening, send us the code below.",
    "notFoundTitle": "That page does not exist",
    "notFoundDescription": "Check the address, or go back to the overview.",
    "forbiddenTitle": "This section is still closed",
    "forbiddenDescription": "It opens once CRM approves your application.",
    "unauthorizedTitle": "Your session ended",
    "unauthorizedDescription": "Sign in again to continue.",
    "crmUnavailableTitle": "CRM is unavailable",
    "crmUnavailableDescription": "Some data will arrive late. Try again in a minute.",
    "reload": "Reload the page",
    "goHome": "Go to overview",
    "correlationId": "Reference code"
  },
  "status": {
    "agent": {
      "online": "Online",
      "offline": "Offline"
    },
    "partner": {
      "PENDING_APPROVAL": "In review",
      "APPROVED": "Approved",
      "REJECTED": "Rejected",
      "SUSPENDED": "Suspended"
    },
    "moderation": {
      "UPLOADED": "Uploaded",
      "SCANNING": "Scanning",
      "SCAN_CLEAN": "Clean",
      "SCAN_FLAGGED": "Threats found",
      "SCAN_FAILED": "Scan failed",
      "READY": "Ready to use"
    },
    "build": {
      "REQUESTED": "Queued",
      "BUILDING": "Building",
      "READY": "Ready",
      "FAILED": "Failed",
      "EXPIRED": "Expired"
    }
  },
  "cookies": {
    "title": "Cookies",
    "body": "Signing in needs one technical cookie holding your session token — the partner space cannot work without it, and it cannot be turned off. Everything else runs only with your consent.",
    "acceptAll": "Accept all",
    "necessaryOnly": "Necessary only"
  },
  "auth": {
    "hero": {
      "eyebrow": "Proxy Seller · partner space",
      "titleLead": "Every agent your SDK brings online,",
      "titleAccent": "counted as yours",
      "titleTail": ".",
      "lead": "Your partner key ties every connected agent to you. Traffic, countries and app moderation in one place, straight from the source.",
      "apply": "Become a partner",
      "chips": {
        "attribution": "partner_key attribution",
        "bytes": "traffic in bytes, UTC",
        "twoFactor": "TOTP sign-in"
      },
      "ticker": {
        "init": "sdk.init · partner_key attached",
        "online": "agent.online · attributed to you",
        "traffic": "traffic · counted in bytes",
        "submitted": "app.submit · sent to moderation",
        "approved": "moderation · approved",
        "countries": "stats · by country"
      }
    },
    "signIn": {
      "title": "Sign in",
      "subtitle": "Your email and password. The second step is a code from your app.",
      "email": "Email",
      "password": "Password",
      "submit": "Sign in",
      "submitting": "Signing in",
      "invalid": "That email and password do not match.",
      "noAccount": "No access yet?",
      "register": "Apply"
    },
    "twoFactor": {
      "title": "Confirm it is you",
      "subtitle": "Open your authenticator app and enter the current code.",
      "code": "Code from your app",
      "recoveryCode": "Recovery code",
      "submit": "Confirm",
      "submitting": "Checking",
      "useRecovery": "Use a recovery code",
      "useCode": "Use a code from the app",
      "invalid": "That code did not work. Wait for the next one and try again.",
      "recoveryInvalid": "That recovery code is unknown or already used.",
      "expired": "The five minutes ran out. Sign in again.",
      "restart": "Back to sign in"
    },
    "register": {
      "title": "Create your space",
      "subtitle": "Enter the partner key CRM issued to you and pick a password.",
      "partnerKey": "Partner key",
      "partnerKeyHint": "The key the SDK uses to attribute agents to you. We check it against CRM and keep it only encrypted.",
      "passwordHint": "At least 12 characters.",
      "submit": "Create space",
      "submitting": "Checking the key",
      "doneTitle": "Your space is ready",
      "doneDescription": "CRM confirmed the key. Sign in — every section is already open.",
      "haveAccount": "Already have access?",
      "signIn": "Sign in"
    },
    "pending": {
      "title": "Application in review",
      "description": "CRM has not approved your application yet. It usually takes one business day.",
      "meanwhile": "Meanwhile you can take the SDK and read the FAQ — enough to start integrating.",
      "checkAgain": "Check again",
      "checking": "Checking",
      "stillPending": "No change yet. We will update the status as soon as CRM answers.",
      "approvedTitle": "Application approved",
      "approvedDescription": "Your space is open — taking you to the overview.",
      "rejectedTitle": "The application was rejected",
      "rejectedDescription": "The reason is below. Fix it and write to us — it can be reviewed again.",
      "suspendedTitle": "Access is suspended",
      "suspendedDescription": "Write to us so we can sort it out.",
      "reason": "Reason",
      "goToSdk": "Open the SDK",
      "goToFaq": "FAQ",
      "signOut": "Sign out"
    },
    "setup": {
      "title": "Two-factor authentication",
      "description": "Turn it on and a stolen password alone is not enough to reach your account.",
      "enabled": "On",
      "disabled": "Off",
      "start": "Turn on",
      "starting": "Preparing",
      "manualSecret": "Key for manual entry",
      "manualHint": "Add this to your app by hand if you are not scanning a code.",
      "confirmCode": "Code from your app",
      "confirm": "Turn on",
      "confirming": "Checking",
      "confirmHint": "Enter the current code so we know the app is set up.",
      "recoveryTitle": "Recovery codes",
      "recoveryDescription": "Ten single-use codes. We show them once — save them now.",
      "download": "Download file",
      "copy": "Copy all",
      "acknowledge": "I saved the codes",
      "acknowledgeHint": "Download or copy the codes to continue.",
      "done": "Done: two-factor authentication is on.",
      "disable": "Turn off",
      "disableHint": "Enter the current code to turn it off.",
      "qrTitle": "QR code",
      "qrHint": "Scan the code with your authenticator app, or enter the key by hand — it is below."
    },
    "errors": {
      "emailRequired": "Enter your email.",
      "emailInvalid": "That address looks like a typo.",
      "partnerKeyRequired": "Paste your partner key.",
      "partnerKeyRejected": "CRM does not know that key and email together. Check both, or write to us.",
      "passwordRequired": "Enter your password.",
      "passwordShort": "That is shorter than 12 characters.",
      "codeFormat": "Six digits.",
      "recoveryRequired": "Enter a recovery code."
    }
  },
  "dashboard": {
    "eyebrow": "Agents · traffic · moderation",
    "title": "Overview",
    "mapTitle": "Where your fleet lives",
    "mapHint": "Shading is agent density. Click a country to see its traffic.",
    "onlineAgents": "Agents online",
    "onlineOf": "of {{total}} registered",
    "trafficPeriod": "Traffic over 30 days",
    "trafficHint": "Last 30 days, including today (UTC)",
    "moderation": "Build in moderation",
    "moderationNone": "Nothing in flight",
    "viewStatistics": "Statistics"
  },
  "fleet": {
    "mapLabel": "Map of the agent fleet by country",
    "legendLow": "Fewer agents",
    "legendHigh": "More",
    "legendHint": "Unfilled countries have no agents",
    "tooltipCounts": "{{online}} online of {{total}}",
    "tooltipTotal": "{{total}} agents",
    "summaryTitle": "Countries",
    "emptyTitle": "No agents yet",
    "emptyDescription": "Build your app with your key and ship it — the first agents show up here within minutes."
  },
  "pagination": {
    "label": "List pages",
    "previous": "Back",
    "next": "Next",
    "page": "Page {{page}}",
    "position": "Page {{page}} of {{pages}} · {{total}} total"
  },
  "statistics": {
    "eyebrow": "Traffic in bytes · UTC",
    "title": "Statistics",
    "presets": "Quick period",
    "preset": {
      "last7d": "7 days",
      "last30d": "30 days",
      "last90d": "90 days"
    },
    "from": "From",
    "to": "To",
    "overTime": "Traffic over time",
    "byCountry": "Breakdown by country",
    "byCountryHint": "Shares come from the backend — we do not recompute them.",
    "breakdownCaption": "Traffic by country for the selected period",
    "traffic": "Traffic",
    "share": "Share",
    "total": "Total for the period",
    "topCountry": "Most traffic",
    "otherCountries": "Other countries",
    "chartLabel": "Traffic chart for the selected period",
    "exportSeries": "CSV: over time",
    "exportCountries": "CSV: by country",
    "csvTimestamp": "Instant (UTC)",
    "csvBytes": "Bytes",
    "csvCountryCode": "Country code",
    "rangeInverted": "The “to” date has to be after the “from” date.",
    "rangeTooLong": "The period cannot exceed {{days}} days.",
    "rangeBlockedTitle": "That period will not work",
    "rangeBlockedDescription": "Shorten the period, and the chart comes back.",
    "emptyTitle": "No traffic in this period",
    "emptyDescription": "Pick another period, or check that your agents are connecting.",
    "usersTitle": "Per-user detail",
    "usersPending": "This section waits on the F10 decision: the backend does not serve it yet."
  },
  "sdk": {
    "eyebrow": "Agent SDK",
    "title": "SDK",
    "description": "Embed the agent in your app and its users become part of the network.",
    "version": "Version",
    "versionLatest": "{{tag}} · latest",
    "versionPrerelease": "{{tag}} · pre-release",
    "versionOlder": "{{tag}} · older version",
    "olderNotice": "This is an older version. Start new integrations on {{tag}}, the latest.",
    "backToLatest": "Show the latest",
    "published": "Published",
    "prerelease": "Pre-release",
    "releaseNotes": "Release notes",
    "downloadTitle": "Download the agent",
    "downloadHint": "Pick the platform and architecture your app ships on. Check the SHA-256 after downloading.",
    "platform": "Platform",
    "arch": "Architecture",
    "platforms": {
      "android": "Android",
      "windows": "Windows",
      "linux": "Linux",
      "macos": "macOS",
      "other": "Other files"
    },
    "arches": {
      "amd64": "x64 (amd64)",
      "arm64": "ARM64",
      "x86": "x86 (32-bit)"
    },
    "kind": {
      "agent": {
        "title": "Standalone agent",
        "hint": "Runs as its own process next to your app."
      },
      "library": {
        "title": "Shared library",
        "hint": "Load it into your app and start the agent from your code."
      },
      "header": {
        "title": "C header",
        "hint": "Declarations for the shared library."
      },
      "aar": {
        "title": "Android library",
        "hint": "Add it to your Android project as a dependency."
      },
      "other": {
        "title": "Other file",
        "hint": "Published with this release."
      }
    },
    "download": "Download",
    "downloadNamed": "Download {{name}}",
    "copyDigest": "Copy the SHA-256 of {{name}}",
    "digestCopied": "Checksum copied",
    "noReleasesTitle": "No release yet",
    "noReleasesDescription": "The SDK has no published release yet. Check back soon, or ask your manager when the first build lands.",
    "emptyTitle": "No files to download",
    "emptyDescription": "This version has no published files yet. Check back later, or pick another version above.",
    "configureTitle": "Configure the agent",
    "configureHint": "Save this as {{name}} next to the agent. It ties every agent you ship to your partner account.",
    "showKey": "Show the key",
    "hideKey": "Hide the key",
    "noKey": "We have no partner key on file for you. Replace YOUR_PARTNER_KEY with the key CRM issued you.",
    "copyEnv": "Copy",
    "envCopied": "Config copied",
    "downloadEnv": "Download {{name}}",
    "runTitle": "Start the agent",
    "runHint": "Load {{name}} into the environment, then start the agent.",
    "runHintAndroid": "Set the variables before you start the agent. Use the agent_key from step 2 in place of YOUR_PARTNER_KEY.",
    "runLibrary": "Using the shared library? Set the same variables from {{name}} in your process environment before you start the agent.",
    "copySnippet": "Copy the code",
    "snippetCopied": "Code copied"
  },
  "faq": {
    "eyebrow": "Help",
    "title": "FAQ",
    "search": "Search the questions",
    "searchPlaceholder": "For example: payouts",
    "cases": "Worked cases",
    "scenario": "What happened",
    "resolution": "What fixed it",
    "emptyTitle": "Nothing matched",
    "emptyDescription": "Try another word, or clear the search — every section is below.",
    "clearSearch": "Clear the search",
    "resultCount": "Found {{count}}",
    "sdkGuide": {
      "title": "How to integrate the Agent SDK",
      "description": "Turn a device into a residential exit node. The recommended path opens the tunnel directly in your own process — connect to a registrator, run a short handshake and start serving traffic. A prebuilt binary is the simpler drop-in.",
      "onboarding": "<b>Onboarding is hands-on.</b> There is no public sandbox or package registry. Your partner key, the balancer address, the fallback list URL and, if you want it, a ready-made reference implementation come from our team directly — contact support to get set up.",
      "copy": "Copy: {{what}}",
      "language": "Language",
      "paths": {
        "title": "Two ways to integrate",
        "intro": "Both speak the same wire protocol to the registrator infrastructure, so either works interchangeably.",
        "options": [
          "<b>Native, direct tunnel in your process — recommended.</b> Open a TCP socket to the registrator, run the <c>TUNL</c> handshake, send an AUTH line, then serve tunnel requests. The whole client is a few dozen lines inside your process, with full control over egress binding and DNS — no subprocess, no external runtime. Reference implementations in Go and Kotlin are available from our team; the source repository is private.",
          "<b>Prebuilt binary or C library — simpler.</b> Ship the standalone binary as a process, or load the C shared library and call <c>StartAgent()</c>. No protocol code to write, but less flexible over egress binding and process integration."
        ]
      },
      "protocol": {
        "title": "The tunnel protocol",
        "intro": "One control connection per agent carries newline-delimited JSON; each tunneled stream rides its own short-lived data connection.",
        "handshake": "<b>Control handshake.</b> Open a TCP connection to the registrator and send a fixed 6-byte header:",
        "handshakeCaption": "Handshake header",
        "columns": {
          "field": "Field",
          "bytes": "Bytes",
          "value": "Value",
          "command": "Command",
          "shape": "Shape",
          "action": "Agent does"
        },
        "fields": {
          "magic": "Magic",
          "version": "Version",
          "connType": "Connection type"
        },
        "auth": "Then send one AUTH line terminated by <c>\\n</c>. The server replies <c>{\"command\":\"AUTH_OK\"}</c> on success. On rejection it closes the connection — you see EOF, and the reason is in the server log. <c>uuid</c> must not be empty.",
        "authLabel": "AUTH line",
        "commands": "<b>Control commands</b>, sent by the server to the agent:",
        "commandsCaption": "Control commands",
        "commandActions": {
          "OPEN": "Dial <c>host:port</c>, open a <b>data</b> connection carrying <c>token</c>, bridge the two.",
          "REBOOT": "Tear down and reconnect."
        },
        "openFail": "If an OPEN cannot be fulfilled, reply on the control connection with:",
        "openFailLabel": "OPEN_FAIL reply",
        "data": "<b>Data connection, one per stream.</b> For each OPEN, dial a <b>second</b> TCP connection to the same registrator, send the 6-byte header with connection type <c>0x02</c>, then the token as <b>32 hex characters with no terminator</b>. From that byte on the socket is a raw pipe — bridge it to the target in both directions until either side closes.",
        "pool": "Optional: keep a small warm pool of pre-connected, pre-handshaked data sockets so an OPEN does not pay a fresh dial round-trip. Evict pooled sockets after about 5 minutes idle — the server closes them by then."
      },
      "connect": {
        "title": "Connect to a tunnel",
        "intro": "Each example runs the whole flow: find a registrator through the balancer, then open the tunnel and authenticate. The balancer is always tried first. The fallback list is used <b>only if that call fails</b> — unreachable, an error, or no registrator — and on every reconnect the balancer is tried first again. This matches the SDK's own balancer-first discovery.",
        "balancing": "When more than one registrator is healthy the samples keep the one reporting the most <c>freeSockets</c>. Treat it as a best-effort tie-break, not real load balancing: on the TCP path the metric is often <c>0</c> everywhere, so selection reduces to the first healthy one. Any registrator with <c>ready</c> and <c>network: \"UP\"</c> is usable.",
        "uuid": "<b>Set a unique uuid per device — it is required, not cosmetic.</b> The UUID is the agent's identity in the registrator. Left empty, the agent falls back to your <b>partner key</b>, so every device shares one identity and each new connection evicts the previous one within about 80 ms — you end up with exactly one live agent. Generate a stable, distinct UUID per device and always set it explicitly.",
        "codeLabel": "connect sample in {{language}}",
        "reference": "A complete reference agent in Go and Kotlin — warm pool, kernel <c>splice(2)</c> bridging, reconnect backoff — exists on our side and can be shared with you directly."
      },
      "serve": {
        "title": "Serving traffic",
        "intro": "After <c>AUTH_OK</c>, the agent serves the server's <c>OPEN</c> commands on the same control connection:",
        "steps": [
          "<b>Receive OPEN</b> with <c>token</c>, <c>host</c> and <c>port</c>, and dial the target <c>host:port</c>.",
          "<b>Open a data connection</b> to the registrator: send the 6-byte header with connection type <c>0x02</c>, then the 32-hex <c>token</c> with no terminator.",
          "<b>Bridge</b> bytes both ways until either side closes. If something fails before bridging, send <c>OPEN_FAIL</c> with the token."
        ],
        "hotPath": "The data socket and each target dial are the hot path — every tunneled byte flows through them. Apply the buffer sizing from “Tuning the tunnel”: set <c>SO_RCVBUF</c>/<c>SO_SNDBUF</c> <b>before</b> these connects and size the bridge copy buffer by profile. A full reference serve loop:",
        "codeLabel": "serve loop in {{language}}",
        "reference": "Our reference agent implements this loop completely, with a warm pool, kernel <c>splice(2)</c> zero-copy bridging and reconnect backoff. It can be shared with you in Go and Kotlin; otherwise mirror the same steps in your language."
      },
      "consume": {
        "title": "Consuming the proxy",
        "intro": "Once an agent is registered, traffic is routed out through it by connecting a SOCKS5 client to the registrator's proxy port. Three identifiers are involved — keep them distinct:",
        "caption": "Identifiers on the buyer side",
        "columns": {
          "identifier": "Identifier",
          "where": "Where",
          "what": "What"
        },
        "identifiers": {
          "agentKey": {
            "name": "<c>partner-agent-key</c>",
            "where": "agent AUTH <c>key</c>",
            "what": "Your secret; authenticates the agent. Never shipped to proxy clients."
          },
          "deviceUuid": {
            "name": "device UUID",
            "where": "agent AUTH <c>uuid</c> = SOCKS <b>login</b>",
            "what": "Per-device identity; also the SOCKS5 username that targets that device."
          },
          "partnerUuid": {
            "name": "partner UUID",
            "where": "SOCKS <b>password</b>",
            "what": "Your account id in the partner config — distinct from the agent key. Provided by our team."
          }
        },
        "socks": "SOCKS5: login is the device UUID, password is the partner UUID, host is the registrator, port is the proxy port we assign, for example <c>8080</c>. The request below prints the agent device's public exit IP:",
        "curlLabel": "SOCKS5 check",
        "onboarding": "The partner UUID and the proxy port are provided by our team during onboarding."
      },
      "production": {
        "title": "Before you ship",
        "intro": "The samples are deliberately minimal to show the protocol. For a fleet-grade agent, add the following — none of it changes the wire protocol, it just makes the client robust.",
        "items": [
          "<b>Set connect and read timeouts</b> on the balancer and <c>/health</c> calls. An unreachable balancer — packets black-holed, not refused — has no default timeout in most HTTP clients and will hang, so the fallback never fires. A timeout is what turns a dead balancer into a fallback.",
          "<b>Read the reply until the newline.</b> The control channel is newline-delimited JSON in both directions; accumulate until <c>\\n</c> rather than doing one fixed read, in case TCP splits the line.",
          "<b>Treat a reply other than AUTH_OK, or a closed socket, as failure</b> — never serve on a dead or unauthenticated socket. Match <c>AUTH_OK</c> by parsing the JSON line, not by a loose substring.",
          "<b>Skip malformed fallback entries and validate the port</b> (1–65535) before dialing, so one bad list entry cannot crash discovery.",
          "<b>Keep the control socket open</b> for the whole session — the same connection carries the <c>OPEN</c> commands after <c>AUTH_OK</c>; tunnel data rides separate short-lived sockets."
        ]
      },
      "tuning": {
        "title": "Tuning the tunnel",
        "intro": "<b>Read this before you ship a custom client.</b> A raw tunnel connects, but untuned it runs slower and less stably than you expect — in our field tests untuned links collapsed to about 5 Mbps on real devices. The values below are proven in production on our Android prototype. They are not applied automatically when you hand-write a client: reproduce them in your integration to get the same throughput.",
        "baseline": "<b>What the reference Go SDK and prebuilt binary already set:</b> Brutal CC target 100 Mbps, QUIC UDP socket buffer 32 MiB, QUIC receive windows 8 MiB per stream and 12 MiB per connection (growing to 32 / 48 MiB), <c>TCP_NODELAY</c> and keep-alive on every socket, kernel auto-tuning and <c>splice(2)</c> on the TCP data path. It does <b>not</b> pin <c>SO_RCVBUF</c>/<c>SO_SNDBUF</c> itself — if you write your own client, add the presets below on top.",
        "presets": "Pick a preset by the link's expected ceiling and move all knobs together — do not tune them independently. <c>LOW_100</c> is the default.",
        "caption": "Field-proven presets",
        "columns": {
          "preset": "Preset",
          "brutal": "Brutal CC (QUIC)",
          "quic": "QUIC UDP buffer",
          "socket": "Socket SO_*BUF",
          "bridge": "TCP bridge buffer",
          "cap": "Per-stream send cap"
        },
        "gotchasTitle": "Gotchas we paid for in field tests:",
        "gotchas": [
          "<b>Set SO_RCVBUF/SO_SNDBUF before connect().</b> TCP window scaling is decided during the SYN handshake, so setting the buffer afterwards does nothing for the effective window. The reference Go SDK applies this ordering only to the QUIC UDP socket and leaves TCP to kernel auto-tuning; in your own client set the TCP buffers before dialing.",
          "<b>Stay in the 1.5–4 MiB safe zone.</b> 12 MiB on a Samsung S Fold 7 and 1 MiB on a Pixel 9 Pro XL each collapsed upload to about 5 Mbps; 1.5 MiB ran clean at 331/299 Mbps. Above <c>net.core.wmem_max</c> or below the <c>tcp_wmem</c> default, an explicit size disables kernel auto-tuning.",
          "<b>Size the QUIC UDP socket buffer to the link, not to the sky.</b> 32 MiB at 100 Mbps is about 2.5 s of bufferbloat. Our prototype runs large flow-control windows (<c>initialMaxData</c> 160 MiB, <c>initialMaxStreamData</c> 16 MiB) and lets the pacer be the rate limit; the Go SDK uses smaller windows (12 MiB per connection, 8 MiB per stream). Keep them generous either way.",
          "<b>Cap the per-stream send buffer at 64–256 KiB</b> so a stalled peer cannot accumulate megabytes that never drain.",
          "<b>TCP: turn TCP_NODELAY on; if you splice, use SPLICE_F_MOVE only.</b> <c>SPLICE_F_MORE</c> re-arms TCP_CORK and caps the pipe at about 2.5 Mbps."
        ],
        "beforeConnect": "<b>Setting the TCP buffers before connect, in Go.</b> The reference Go SDK relies on kernel auto-tuning; add this if you hand-write a client and want the prototype's fixed buffer sizes:",
        "codeLabel": "Go buffer setup",
        "equivalents": "Call the equivalent — <c>setsockopt(SO_RCVBUF/SO_SNDBUF)</c> in C, <c>Socket.receiveBufferSize</c>/<c>sendBufferSize</c> in Java and Kotlin — <b>before</b> the connect on every tunnel socket."
      },
      "binary": {
        "title": "Prebuilt binary or C library",
        "intro": "Every release ships standalone binaries and C shared libraries for Linux (amd64, x86, arm64) and Windows (amd64). Settings come from environment variables or flags.",
        "standalone": "<b>Standalone binary</b> — balancer-discovered, with a fallback list used if the balancer is unreachable:",
        "runLabel": "binary command",
        "clib": "<b>C shared library.</b> Each release generates the <c>.so</c>/<c>.dll</c> together with its own C header, named per artifact — for example <c>libproxy_agent.h</c>. Include the header shipped with the library you link. <c>StartAgent()</c> does not block; <c>StopAgent()</c> shuts down gracefully.",
        "clibLabel": "C library usage"
      },
      "config": {
        "title": "Configuration reference",
        "precedence": "Applies to both the native and the prebuilt path. Flags win over environment variables, which win over defaults.",
        "connection": "Connection",
        "tuning": "Tuning",
        "required": "Required",
        "optional": "Optional",
        "columns": {
          "key": "Key",
          "required": "Required",
          "default": "Default",
          "description": "Description"
        },
        "settings": {
          "agent_key": "Partner authentication key, sent in the AUTH line. Aliases: <c>AGENT_KEY</c>, <c>key</c>, <c>KEY</c>.",
          "agent_uuid": "Unique device identity. Must be distinct per device.",
          "balancer_host": "Balancer IP and port, given to you by our team. The agent calls <c>GET /getRegistrator</c> to obtain a registrator. The port is typically <c>1005</c>.",
          "fallback_file_url": "JSON registrator list, probed when the balancer is unreachable.",
          "dns_servers": "Comma- or space-separated resolvers. Overrides the system resolver. Alias: <c>DNS_SERVERS</c>.",
          "http_timeout_ms": "Timeout for the balancer and fallback HTTP calls.",
          "enable_heartbeat": "Periodic liveness tick.",
          "enable_systeminfo": "Collect OS, architecture and CPU info at registration."
        }
      },
      "verify": {
        "title": "Verifying the connection",
        "steps": [
          "<b>The handshake succeeds</b> — your client reads <c>{\"command\":\"AUTH_OK\"}</c>. A closed connection with no reply means the key was rejected; check the server log.",
          "<b>The registrator sees the agent</b> — its health snapshot reports a rising <c>agentCount</c>.",
          "<b>Route a request</b> — connect a SOCKS5 client to the registrator proxy port (login is the agent UUID, password is the partner UUID) and fetch your public IP; it should be the device's exit IP."
        ]
      }
    },
    "grafana": {
      "title": "How to connect Grafana",
      "description": "You need access to the server Grafana runs on and the right to restart it. It takes about ten minutes.",
      "copyStep": "Copy: {{step}}",
      "steps": {
        "bundle": {
          "title": "Build and download the bundle",
          "body": "On the Grafana plugin page press “Build a bundle” and download the archive once the build is ready. The build history on that page shows how long the link stays valid."
        },
        "plugin": {
          "title": "Install the ClickHouse plugin",
          "body": "The plugin is distributed from its own repository. Take the release archive and install it, putting the archive link in place of PLUGIN_ARCHIVE_URL. Without the CLI, unpack the archive into Grafana's plugin directory — usually /var/lib/grafana/plugins."
        },
        "unsigned": {
          "title": "Allow the unsigned plugin",
          "body": "The plugin is not signed, so Grafana will not load it unless you allow it explicitly. Add this to grafana.ini:"
        },
        "token": {
          "title": "Give Grafana your gateway token",
          "body": "An operator issues the token — if you do not have one, ask your account manager. Set it as an environment variable of the Grafana process in place of YOUR_GATEWAY_TOKEN; if Grafana runs as a service, put it in that service's environment file. The bundle reads the token from there, so it never has to be written into a file."
        },
        "provisioning": {
          "title": "Install the datasource file",
          "body": "Unpack the bundle and copy the datasource file into Grafana's provisioning directory:"
        },
        "restart": {
          "title": "Restart Grafana",
          "body": "Grafana reads plugins and provisioning only at startup."
        },
        "check": {
          "title": "Check the connection",
          "body": "In Grafana, open Connections → Data sources — the “ClickHouse Partners” datasource is there. It is read-only: its settings come from the provisioning file, not the UI. Open it and press Test."
        },
        "explore": {
          "title": "Open your data",
          "body": "Go to Explore, pick “ClickHouse Partners” and query your traffic — or build a dashboard on this datasource. You only ever see your own agents' traffic: row isolation is enforced inside ClickHouse."
        }
      },
      "docker": {
        "title": "If Grafana runs in Docker",
        "body": "Steps 2–6 come down to one command. Run it in the directory you unpacked the bundle into, with the plugin unpacked into a plugins subdirectory next to it."
      },
      "troubleshoot": {
        "title": "If you see no data",
        "pluginMissing": "The datasource is not listed — Grafana did not load the plugin. Check the allow_loading_unsigned_plugins line and the plugin directory, then read Grafana's log from startup.",
        "testFails": "Test returns an authorisation error — the token did not reach Grafana. Make sure PROXYBAND_GATEWAY_TOKEN is set for the process that runs Grafana, then restart it.",
        "noData": "The connection works but the charts are empty — your agents have not reported traffic yet. Check that the build passed moderation and is live with users, and widen the time range in Grafana."
      }
    }
  },
  "moderation": {
    "eyebrow": "Build review",
    "title": "Moderation",
    "description": "Upload an agent build: VirusTotal scans it, we compute its SHA-256 and register that agent hash in CRM — then the build is ready to use.",
    "file": "Build file",
    "fileHint": "Set the platform and version first. The file — APK, IPA or EXE up to 200 MB — is sent as soon as you choose it.",
    "platform": "Platform",
    "appVersion": "App version",
    "appVersionHint": "For example, 2.4.1",
    "appVersionRequired": "Enter the app version.",
    "upload": "Choose a file and send for scanning",
    "uploading": "Uploading",
    "progress": "{{percent}}% uploaded",
    "uploaded": "Build accepted — scanning now.",
    "historyTitle": "Submitted builds",
    "caption": "Build history and state",
    "fileName": "File",
    "size": "Size",
    "sent": "Sent",
    "engines": "What fired",
    "enginesHint": "This is what VirusTotal called it. Rebuild without the packer and upload again.",
    "lastError": "Why the scan failed",
    "terminalFlagged": "This build's hash never reaches CRM — that state is terminal. Upload a fixed build.",
    "terminalFailed": "The scan never finished. Upload the build again.",
    "newBuild": "Upload a new build",
    "details": "Details",
    "hideDetails": "Collapse",
    "emptyTitle": "No builds yet",
    "emptyDescription": "Upload the first one — scanning takes a couple of minutes.",
    "noDownload": "Uploaded files are never served back: we keep them outside anything reachable from the web.",
    "agentHash": "Agent hash",
    "agentHashFull": "Agent hash (SHA-256)",
    "agentHashHint": "Computed from the uploaded file — this is the hash we send to CRM.",
    "readyNote": "The hash is registered in CRM — the build is ready to use."
  },
  "plugin": {
    "eyebrow": "Grafana · ClickHouse",
    "title": "Grafana plugin",
    "description": "We build a bundle: datasource settings and a README, plus your gateway token and the traffic viewer app for the chosen system when they are available. It never contains your partner key.",
    "requirementsTitle": "Requirements",
    "requirementsOsLabel": "OS",
    "requirementsOs": "Windows, macOS or Linux, x86-64 or ARM64",
    "requirementsGrafanaLabel": "Grafana (optional)",
    "requirementsGrafana": "{{minGrafanaVersion}} or newer, local install as an app or service. Docker and Grafana Cloud are not supported.",
    "requirementsAdminLabel": "Administrator rights",
    "requirementsAdmin": "required only to change an existing Grafana",
    "installTitle": "Installation",
    "installExistingLabel": "Existing Grafana {{minGrafanaVersion}}+",
    "installExisting": "ProxyBand ClickHouse plugin {{pluginVersion}} and the data source are added, the unsigned plugin is allowed, Grafana is restarted. Asks for confirmation first.",
    "installDownloadLabel": "No suitable Grafana",
    "installDownload": "Grafana {{grafanaVersion}} is downloaded from grafana.com ({{downloadSize}}, needs about {{freeSpace}} of free disk space) into a separate folder.",
    "installNothingElse": "No other system changes.",
    "noKey": "Your partner key is not in the bundle: CRM needs it, while Grafana authenticates with the gateway token.",
    "request": "Build a bundle",
    "requesting": "Sending",
    "requested": "Request accepted — building the bundle.",
    "building": "Building the bundle, this takes a few seconds.",
    "download": "Download the bundle",
    "downloading": "Preparing the file",
    "downloaded": "Bundle downloaded",
    "expiredTitle": "That link no longer works",
    "expiredDescription": "Bundles live for a limited time. Build a new one — it is quick.",
    "caption": "Bundle build history",
    "created": "Requested",
    "expires": "Valid until",
    "checksum": "Checksum",
    "downloads": "Downloads",
    "size": "Size",
    "platform": "Platform",
    "buildVersion": "Build version",
    "platforms": {
      "darwin-arm64": "macOS · Apple silicon",
      "darwin-amd64": "macOS · Intel",
      "linux-amd64": "Linux · x86-64",
      "linux-arm64": "Linux · ARM64",
      "windows-amd64": "Windows · x86-64",
      "windows-arm64": "Windows · ARM64"
    },
    "platformGuessed": "Your browser does not report the processor, so {{platform}} is a guess. Change it if this computer is different.",
    "platformGuessedMac": "Your browser does not tell Apple silicon from Intel, so {{platform}} is picked. Apple menu → About This Mac shows Chip on Apple silicon and Processor on Intel.",
    "emptyTitle": "No bundles yet",
    "emptyDescription": "Build the first one — you need it to point Grafana at your data.",
    "failedTitle": "The build failed"
  },
  "terms": {
    "title": "Terms and conditions",
    "description": "Read the partner programme terms (version of {{version}}). You accept them once, until they next change.",
    "documentLabel": "Terms and conditions text",
    "declineHint": "You can decline and keep using the partner space, but build moderation, the SDK and the Grafana plugin stay unavailable until you accept.",
    "accept": "I accept the terms",
    "decline": "Decline",
    "accepted": "Terms accepted — every section is available.",
    "outdated": "The terms changed while you were reading. Reload the page and read the new version.",
    "gateTitle": "Terms and conditions not accepted",
    "gateModeration": "Submitting builds for moderation opens as soon as you accept the terms.",
    "gateSdk": "Downloads and the agent config open as soon as you accept the terms.",
    "gatePlugin": "Building and downloading the Grafana bundle open as soon as you accept the terms.",
    "review": "Read and accept"
  },
  "mocks": {
    "label": "Demo data",
    "description": "Responses are served locally; the backend is not involved."
  }
}
